<template>
  <div class="uph-card">
    <!-- ── Header ───────────────────────────────────────────── -->
    <div class="uph-head">
      <span class="uph-accent-bar" />
      <div class="uph-head-text">
        <div class="uph-eyebrow">Unit price history</div>
        <div class="uph-head-row">
          <a
            v-if="data && data.mls_floor_plan_feature"
            href="#"
            v-b-modal="'mls-fp-' + data.mls_floor_plan_feature.fid"
            class="uph-unit-link"
            @click.prevent
          >Unit {{ data && data.mls_address }}</a>
          <span v-else class="uph-unit-link uph-unit-link--plain">
            Unit {{ data && data.mls_address }}
          </span>
          <span class="uph-sub-badge">{{ subTitle }}</span>
          <span class="uph-listed">Listed {{ data && data.mls_list_date }}</span>
        </div>
      </div>
      <b-button
        v-if="current_user.role === 'admin'"
        v-b-modal="'mls-entry-new'"
        class="uph-add-btn"
        @click="$emit('set-project-id', data.cid)"
      >
        <font-awesome-icon :icon="['fas', 'plus']" />
        Add entry
      </b-button>
      <button class="uph-close-btn" title="Close" @click="$emit('close-modal')">
        <font-awesome-icon :icon="['fas', 'times']" />
      </button>
    </div>

    <!-- ── Spec strip ───────────────────────────────────────── -->
    <div class="uph-specs">
      <div
        v-for="(spec, i) in specs"
        :key="spec.label"
        class="uph-spec-group"
      >
        <span v-if="i > 0" class="uph-spec-divider" />
        <div class="uph-spec">
          <div class="uph-spec-label">{{ spec.label }}</div>
          <div class="uph-spec-value" :class="{ 'uph-spec-value--cap': spec.cap }">{{ spec.value }}</div>
        </div>
      </div>
    </div>

    <!-- ── Summary band ─────────────────────────────────────── -->
    <div class="uph-summary">
      <div class="uph-stat">
        <div class="uph-stat-label">Original list</div>
        <div class="uph-stat-val">{{ summary.originalList }}</div>
      </div>
      <div class="uph-stat">
        <div class="uph-stat-label">Net price</div>
        <div class="uph-stat-row">
          <span class="uph-stat-val">{{ summary.netPrice }}</span>
          <span v-if="summary.netPsf" class="uph-stat-sub">{{ summary.netPsf }}</span>
        </div>
      </div>
      <div class="uph-stat">
        <div class="uph-stat-label">Incentives</div>
        <div class="uph-stat-val">{{ summary.incentives }}</div>
      </div>
      <div class="uph-stat">
        <div class="uph-stat-label">Days on market</div>
        <div class="uph-stat-val">{{ summary.dom }}</div>
      </div>
      <div class="uph-stat">
        <div class="uph-stat-label">Sold</div>
        <div class="uph-stat-row">
          <span class="uph-stat-val uph-stat-val--success">{{ summary.soldShort }}</span>
          <span v-if="summary.soldYear" class="uph-stat-sub">{{ summary.soldYear }}</span>
        </div>
      </div>
    </div>

    <!-- ── Table ────────────────────────────────────────────── -->
    <div class="uph-table-wrap">
      <table class="uph-table">
        <thead>
          <tr>
            <th class="uph-th uph-th--first">Status</th>
            <th class="uph-th">Price date</th>
            <th class="uph-th uph-th--right">DOM</th>
            <th class="uph-th uph-th--right">List price</th>
            <th class="uph-th uph-th--right">$PSF</th>
            <th class="uph-th uph-th--right">Incentives</th>
            <th class="uph-th uph-th--right">Net price</th>
            <th class="uph-th">Sold date</th>
            <th class="uph-th" :class="{ 'uph-th--last': !isAdmin }">Listing type</th>
            <th v-if="isAdmin" class="uph-th uph-th--last">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, i) in rows"
            :key="'r-' + i"
            class="uph-row"
            :class="{ 'uph-row--sold': row.isSold }"
          >
            <td class="uph-td uph-td--first">
              <span class="uph-pill" :class="{ 'uph-pill--sold': row.isSold }">
                <span class="uph-pill-dot" />
                {{ row.status }}
              </span>
            </td>
            <td class="uph-td">{{ row.priceDate || '–' }}</td>
            <td class="uph-td uph-td--right uph-td--muted">{{ row.dom != null ? row.dom : '–' }}</td>
            <td class="uph-td uph-td--right uph-td--medium">
              <span v-if="row.listPrice" class="uph-list-cell">
                {{ row.listPrice }}
                <span v-if="row.delta" class="uph-delta" :class="row.delta < 0 ? 'uph-delta--down' : 'uph-delta--up'">
                  <font-awesome-icon :icon="['fas', row.delta < 0 ? 'arrow-down' : 'arrow-up']" />
                  {{ row.deltaLabel }}
                </span>
              </span>
              <span v-else class="uph-td--muted">–</span>
            </td>
            <td class="uph-td uph-td--right uph-td--muted">{{ row.psf || '–' }}</td>
            <td class="uph-td uph-td--right" :class="row.incentive ? 'uph-td--accent' : 'uph-td--muted'">
              {{ row.incentive || '–' }}
            </td>
            <td class="uph-td uph-td--right uph-td--net">{{ row.netPrice || '–' }}</td>
            <td class="uph-td" :class="{ 'uph-td--muted': !row.soldDate }">{{ row.soldDate || '–' }}</td>
            <td class="uph-td">
              <span v-if="row.listingType" :class="listingTypePillClass(row.listingType)">
                <span class="rpd-lt-pill__dot"></span>{{ row.listingType }}
              </span>
              <span v-else class="uph-td--muted">&#8212;</span>
            </td>
            <td v-if="isAdmin" class="uph-td uph-td--last uph-td--actions">
              <button
                class="uph-action-btn"
                title="Edit entry"
                @click="$emit('editMLSEntryNew', { projectId: row.cid, projectEntry: row.entry })"
              >
                <font-awesome-icon :icon="['fas', 'edit']" />
              </button>
              <button
                class="uph-action-btn uph-action-btn--danger"
                title="Delete entry"
                @click="$emit('deleteMLSEntry', { projectId: row.cid, projectEntry: row.entry })"
              >
                <font-awesome-icon :icon="['fas', 'trash']" />
              </button>
              <a
                v-if="row.backup_file"
                :href="row.backup_file"
                target="_blank"
                class="uph-action-btn"
                title="Backup file"
              >
                <font-awesome-icon :icon="['fas', 'file-alt']" />
              </a>
            </td>
          </tr>
          <tr v-if="!rows.length">
            <td :colspan="isAdmin ? 10 : 9" class="uph-empty">No price events recorded.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ── Footer ───────────────────────────────────────────── -->
    <div class="uph-foot">
      <span class="uph-foot-text">
        <strong>{{ rows.length }}</strong> price {{ rows.length === 1 ? 'event' : 'events' }}
        <span v-if="summary.range"> · {{ summary.range }}</span>
      </span>
      <div class="uph-spacer" />
      <button class="uph-btn uph-btn--ghost" @click="exportPdf">
        <font-awesome-icon :icon="['fas', 'download']" />
        Export PDF
      </button>
      <button class="uph-btn uph-btn--navy" @click="$emit('close-modal')">
        <font-awesome-icon :icon="['fas', 'times']" />
        Close
      </button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, computed } from 'vue';
import { useUserStore } from '../../store/UserStore';
import { useRouter } from 'vue2-helpers/vue-router';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { listingTypePillClass, listingTypePdfColors } from '../../composables/listingTypePill';

const current_user = ref(useUserStore().current_user);
const route = useRouter();
const props = defineProps({
  data: null,
  history: null,
  isMarketStrata: null,
});

defineEmits(['set-project-id', 'editMLSEntryNew', 'deleteMLSEntry', 'close-modal', 'export-pdf']);

const isAdmin = computed(() => current_user.value && current_user.value.role === 'admin');

const isRealValue = (value) => {
  return !['', '-', NaN, 0, null, 'NaN', undefined].includes(value);
};

const fmtMoney = (n) => {
  const num = Number(n);
  if (!num || isNaN(num)) return null;
  return '$' + Math.round(num).toLocaleString('en-US');
};

const fmtPsf = (n) => {
  const num = Number(n);
  if (!num || isNaN(num)) return null;
  return props.isMarketStrata
    ? '$' + Math.round(num).toLocaleString('en-US')
    : '$' + num.toFixed(2);
};

const fmtCompactDate = (val) => {
  if (!val || val === '-' || val === 'Invalid Date') return null;
  const d = new Date(val);
  if (isNaN(d.getTime())) return val;
  return d.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: '2-digit' });
};

const subTitle = computed(() => {
  if (!props.data) return '';
  const fp = props.data.custom_field_mls_beds_value;
  if (fp && fp.type) return fp.type + (fp.fp_specs ? ' / ' + fp.fp_specs : '');
  return props.data.mls_beds ? props.data.mls_beds + '-bed' : '';
});

const specs = computed(() => {
  const d = props.data || {};
  return [
    { label: 'Beds', value: d.mls_beds || '–', cap: true },
    { label: 'Baths', value: d.mls_baths || '–' },
    { label: 'Unit size', value: d.mls_unit_size ? Math.round(d.mls_unit_size).toLocaleString() + ' sqft' : '–' },
    { label: 'View', value: d.orientation || '–' },
    { label: 'Levels', value: d.levels || '–' },
    { label: 'Parking', value: d.stalls ? d.stalls : '–' },
    { label: 'Stalls', value: d.mls_parking ? d.mls_parking : '–' },
  ];
});

const setListingType = (item, index) => {
  const list = props.history || [];
  let res = '';
  const priceDate = new Date(item.mls_price_date);
  const soldDate = new Date(item.mls_sold_date);
  const constructionCompletionDate = new Date(props.data && props.data.mls_import_date);
  const soldOutDate = new Date(props.data && props.data.sold_out_date);
  let hasSoldStatusBeforeEntry = false;
  if (index > 0) {
    for (let i = index - 1; i >= 0; i--) {
      if (list[i].mls_status && list[i].mls_status.toLowerCase() === 'sold') {
        hasSoldStatusBeforeEntry = true;
        break;
      }
    }
  }
  res = 'Presale';
  if (hasSoldStatusBeforeEntry) {
    if ([priceDate, soldDate].some((date) => date < constructionCompletionDate)) res = 'Assignment';
    if ([priceDate, soldDate].some((date) => date > constructionCompletionDate)) res = 'Resale';
  } else {
    if ([priceDate, soldDate].some((date) => date > soldOutDate && date < constructionCompletionDate)) res = 'Assignment';
    if ([priceDate, soldDate].some((date) => date > soldOutDate && date > constructionCompletionDate)) res = 'Resale';
  }
  if (!props.isMarketStrata) {
    if (res === 'Presale' || res === 'Assignment') res = 'New Lease'; // rentals have no assignments
    if (res === 'Resale') res = 'Re-Lease';
  }
  return res;
};

const rows = computed(() => {
  const list = props.history || [];
  let prevList = null;
  return list.map((item, i) => {
    const rawList = isRealValue(item.mls_list_price) ? Number(item.mls_list_price) : null;
    const delta = rawList != null && prevList != null && rawList !== prevList ? rawList - prevList : 0;
    if (rawList != null) prevList = rawList;
    return {
      ...item,
      isSold: item.mls_status && item.mls_status.toLowerCase() === 'sold',
      status: item.mls_status || '–',
      priceDate: fmtCompactDate(item.mls_price_date),
      dom: isRealValue(item.mls_dom) ? item.mls_dom : null,
      listPrice: fmtMoney(item.mls_list_price),
      psf: fmtPsf(item.mls_psf),
      incentive: fmtMoney(item.buyer_incentives),
      netPrice: fmtMoney(item.net_price),
      soldDate: fmtCompactDate(item.mls_sold_date),
      listingType: setListingType(item, i),
      delta,
      deltaLabel: delta ? '$' + Math.abs(delta).toLocaleString('en-US') : '',
    };
  });
});

const summary = computed(() => {
  const list = (props.history || []).slice();
  const priced = list.filter((r) => isRealValue(r.mls_list_price));
  const firstList = priced.length ? Number(priced[0].mls_list_price) : null;
  const lastPriced = priced.length ? priced[priced.length - 1] : null;
  const lastNet = lastPriced && isRealValue(lastPriced.net_price) ? Number(lastPriced.net_price) : null;
  const lastPsf = lastPriced && isRealValue(lastPriced.mls_psf) ? lastPriced.mls_psf : null;
  const incentiveTotal = list.reduce((acc, r) => acc + (isRealValue(r.buyer_incentives) ? Number(r.buyer_incentives) : 0), 0);
  const soldRow = list.find((r) => r.mls_status && r.mls_status.toLowerCase() === 'sold');
  const lastDom = (() => {
    for (let i = list.length - 1; i >= 0; i--) {
      if (isRealValue(list[i].mls_dom)) return list[i].mls_dom;
    }
    return null;
  })();

  let soldShort = '–';
  let soldYear = '';
  if (soldRow && soldRow.mls_sold_date && soldRow.mls_sold_date !== '-') {
    const d = new Date(soldRow.mls_sold_date);
    if (!isNaN(d.getTime())) {
      soldShort = d.toLocaleDateString('en-US', { day: '2-digit', month: 'short' });
      soldYear = String(d.getFullYear());
    }
  }

  let range = '';
  const dates = list
    .map((r) => new Date(r.mls_price_date))
    .filter((d) => !isNaN(d.getTime()));
  if (dates.length) {
    const min = new Date(Math.min(...dates.map((d) => d.getTime())));
    const max = new Date(Math.max(...dates.map((d) => d.getTime())));
    range = min.toLocaleDateString('en-US', { day: '2-digit', month: 'short' })
      + ' – ' + max.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  return {
    originalList: firstList != null ? '$' + Math.round(firstList).toLocaleString('en-US') : '–',
    netPrice: lastNet != null ? '$' + Math.round(lastNet).toLocaleString('en-US') : '–',
    netPsf: lastPsf != null ? (props.isMarketStrata ? '$' + Math.round(lastPsf) : '$' + Number(lastPsf).toFixed(2)) + '/sqft' : '',
    incentives: incentiveTotal > 0 ? '$' + Math.round(incentiveTotal).toLocaleString('en-US') : '–',
    dom: lastDom != null ? lastDom : '–',
    soldShort,
    soldYear,
    range,
  };
});

const exportPdf = () => {
  try {
    const d = props.data || {};
    const list = props.history || [];
    const tableRows = rows.value;
    const sum = summary.value;
    const now = new Date();
    const reportDate = now.toLocaleDateString('en-US', {
      day: '2-digit', month: 'short', year: 'numeric',
    }).toUpperCase();
    const reportTime = now.toLocaleTimeString('en-US', {
      hour: '2-digit', minute: '2-digit', hour12: false,
    });

    const doc = new jsPDF({ unit: 'mm', format: 'letter', orientation: 'portrait' });
    const PW = doc.internal.pageSize.width;
    const PH = doc.internal.pageSize.height;

    const C = {
      navy: [18, 40, 63],
      coral: [252, 88, 104],
      violet: [52, 51, 118],
      fg1: [18, 40, 63],
      fg2: [84, 84, 84],
      fg3: [138, 138, 138],
      success: [31, 157, 85],
      successBg: [232, 245, 238],
      danger: [212, 56, 71],
      dangerBg: [253, 234, 236],
      warning: [217, 119, 6],
      warningBg: [253, 241, 222],
      divider: [238, 240, 244],
      borderStrong: [201, 204, 210],
      bgSubtle: [237, 242, 254],
    };
    const M = { l: 14, r: 14 };

    const drawUtilBar = () => {
      doc.setFillColor(...C.navy);
      doc.rect(0, 0, PW, 9, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(255, 255, 255);
      const reportLabel = `REPORT · ${reportDate}`;
      doc.text(reportLabel, M.l, 5.8);
      doc.text(reportTime, M.l + doc.getTextWidth(reportLabel) + 8, 5.8);
      const brandTxt = 'REALPROPERTYDATA.CA';
      doc.text(brandTxt, PW - M.r, 5.8, { align: 'right' });
      doc.setFillColor(...C.coral);
      doc.circle(PW - M.r - doc.getTextWidth(brandTxt) - 2.5, 4.6, 0.9, 'F');
    };

    const drawPageFooter = (pageNum, total) => {
      const fy = PH - 10;
      doc.setDrawColor(...C.divider);
      doc.setLineWidth(0.2);
      doc.line(M.l, fy - 4, PW - M.r, fy - 4);
      doc.setFillColor(...C.coral);
      doc.circle(M.l + 1.2, fy - 1, 0.9, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.5);
      doc.setTextColor(...C.fg3);
      doc.text('REAL PROPERTY DATA · CONFIDENTIAL', M.l + 3.5, fy);
      doc.text(`PAGE ${pageNum} OF ${total}`, PW - M.r, fy, { align: 'right' });
    };

    // ====== HEADER ======
    let y = 14;

    // Coral accent bar
    doc.setFillColor(...C.coral);
    doc.rect(M.l, y, 10, 1.2, 'F');
    y += 4.5;

    // Eyebrow
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...C.violet);
    const eyebrow = `UNIT PRICE HISTORY${subTitle.value ? ' · ' + subTitle.value.toUpperCase() : ''}`;
    doc.text(eyebrow, M.l, y);

    // Title
    const title = `Unit ${d.mls_address || '—'}`;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.setTextColor(...C.fg1);
    doc.text(title, M.l, y + 7);

    // Right block
    let ay = y;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...C.violet);
    doc.text('LISTED', PW - M.r, ay, { align: 'right' });
    ay += 4.5;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...C.fg1);
    doc.text(String(d.mls_list_date || '—'), PW - M.r, ay, { align: 'right' });
    ay += 4.5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...C.fg2);
    if (d.mls_plan_name && d.mls_plan_name !== '-') {
      doc.text(`Plan · ${d.mls_plan_name}`, PW - M.r, ay, { align: 'right' });
      ay += 4.5;
    }
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(7);
    doc.setTextColor(...C.fg3);
    doc.text(`${list.length} ${list.length === 1 ? 'event' : 'events'} tracked`, PW - M.r, ay, { align: 'right' });

    y = Math.max(y + 11, ay) + 3;
    doc.setDrawColor(...C.divider);
    doc.setLineWidth(0.2);
    doc.line(M.l, y, PW - M.r, y);
    y += 7;

    // ====== TWO-COLUMN OVERVIEW ======
    const ovW = PW - M.l - M.r;
    const gap = 6;
    const specsW = (ovW - gap) * (1 / 2.05);
    const kpiW = (ovW - gap) * (1.05 / 2.05);
    const kpiX = M.l + specsW + gap;

    const drawEyebrow = (x, w, txt) => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.5);
      doc.setTextColor(...C.fg3);
      doc.text(txt, x, y);
      const ew = doc.getTextWidth(txt);
      doc.setDrawColor(...C.divider);
      doc.setLineWidth(0.25);
      doc.line(x + ew + 2, y - 0.8, x + w, y - 0.8);
    };
    drawEyebrow(M.l, specsW, 'UNIT PROFILE');
    drawEyebrow(kpiX, kpiW, 'PERFORMANCE SNAPSHOT');
    y += 5;

    // ---- Specs (left) ----
    const fmt = (val) => {
      if (val == null || val === '' || val === '-') return '—';
      return String(val);
    };
    const bedsTxt = (() => {
      const b = d.mls_beds;
      if (!b || b === '-') return '—';
      const s = String(b).toLowerCase();
      if (s === 's') return 'Studio';
      if (s === 'jr') return 'Jr-Bed';
      return s + '-bed';
    })();
    const specRows = [
      { label: 'Beds', value: bedsTxt },
      { label: 'Baths', value: fmt(d.mls_baths) },
      { label: 'Unit size', value: d.mls_unit_size ? Math.round(d.mls_unit_size).toLocaleString() + ' sqft' : '—' },
      { label: 'View', value: fmt(d.orientation) },
      { label: 'Levels', value: fmt(d.levels) },
      { label: 'Parking', value: fmt(d.stalls) },
      { label: 'Stalls', value: fmt(d.mls_parking) },
    ];

    let sy = y;
    const specValueX = M.l + 28;
    const specValueMaxW = specsW - 28;
    specRows.forEach(s => {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(...C.fg2);
      doc.text(s.label, M.l, sy);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.2);
      doc.setTextColor(...(s.value === '—' ? C.fg3 : C.fg1));
      const t = doc.splitTextToSize(String(s.value || '—'), specValueMaxW)[0] || '—';
      doc.text(t, specValueX, sy);
      sy += 5.5;
    });

    // ---- KPI grid 3x2 (right) ----
    const kpis = [
      { label: 'Original list', value: sum.originalList, featured: true },
      { label: 'Net price', value: sum.netPrice, sub: sum.netPsf },
      { label: 'Incentives', value: sum.incentives, sub: sum.incentives !== '–' ? 'Buyer incentives' : '' },
      { label: 'Days on market', value: String(sum.dom), sub: 'DOM' },
      { label: 'Sold', value: sum.soldShort, sub: sum.soldYear, isSuccess: sum.soldShort !== '–' },
      { label: 'Events', value: String(list.length), sub: list.length ? 'price updates' : 'no events' },
    ];

    const kCols = 3, kRows = 2;
    const cellW = kpiW / kCols;
    const cellH = 14;
    const gridY = y - 0.5;
    doc.setDrawColor(...C.divider);
    doc.setLineWidth(0.25);
    doc.roundedRect(kpiX, gridY, kpiW, cellH * kRows, 1.5, 1.5);
    kpis.forEach((k, i) => {
      const col = i % kCols, row = Math.floor(i / kCols);
      const cx = kpiX + col * cellW;
      const cy = gridY + row * cellH;
      if (k.featured) {
        doc.setFillColor(...C.navy);
        doc.rect(cx, cy, cellW, cellH, 'F');
      }
      doc.setDrawColor(...C.divider);
      doc.setLineWidth(0.2);
      if (col > 0) doc.line(cx, cy + 0.5, cx, cy + cellH - 0.5);
      if (row > 0) doc.line(cx + 0.5, cy, cx + cellW - 0.5, cy);

      const isMuted = !k.value || k.value === '–' || k.value === '—';
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(5.6);
      if (k.featured) doc.setTextColor(255, 255, 255);
      else doc.setTextColor(...C.fg2);
      doc.text(k.label.toUpperCase(), cx + 2.5, cy + 3.8);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      if (k.featured) doc.setTextColor(255, 255, 255);
      else if (k.isSuccess) doc.setTextColor(...C.success);
      else doc.setTextColor(...(isMuted ? C.fg3 : C.fg1));
      doc.text(String(k.value || '—'), cx + 2.5, cy + 9);

      if (k.sub) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(5.5);
        if (k.featured) doc.setTextColor(255, 255, 255);
        else doc.setTextColor(...C.fg3);
        doc.text(String(k.sub), cx + 2.5, cy + 12.5);
      }
    });

    y = Math.max(sy + 1, gridY + cellH * kRows + 6) + 4;

    // ====== PRICE EVENTS TABLE ======
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(...C.fg1);
    doc.text('Price events', M.l, y);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...C.fg3);
    const subRight = sum.range
      ? `${list.length} EVENTS · ${sum.range.toUpperCase()}`
      : `${list.length} EVENTS`;
    doc.text(subRight, PW - M.r, y, { align: 'right' });
    y += 3;

    const headers = ['Status', 'Price Date', 'DOM', 'List Price', '$PSF', 'Incentives', 'Net Price', 'Sold Date', 'Listing Type'];
    const rowMeta = tableRows.map(r => ({
      isSold: r.isSold,
      listingType: r.listingType,
      delta: r.delta,
      deltaLabel: r.deltaLabel,
      status: r.status,
    }));

    const body = tableRows.map(r => [
      r.status || '—',
      r.priceDate || '—',
      r.dom != null ? String(r.dom) : '—',
      r.listPrice || '—',
      r.psf || '—',
      r.incentive || '—',
      r.netPrice || '—',
      r.soldDate || '—',
      r.listingType || '—',
    ]);

    const baseFontSize = 7.6;

    autoTable(doc, {
      startY: y,
      margin: { left: M.l, right: M.r, top: 14, bottom: 18 },
      head: [headers],
      body,
      theme: 'plain',
      styles: {
        font: 'helvetica', fontSize: baseFontSize,
        cellPadding: { top: 2.6, right: 2, bottom: 2.6, left: 2 },
        textColor: C.fg1, overflow: 'linebreak',
      },
      headStyles: {
        fontStyle: 'bold', fontSize: 6.5, textColor: C.fg2,
        halign: 'right', overflow: 'linebreak', valign: 'bottom',
        cellPadding: { top: 2, right: 2, bottom: 2.5, left: 2 },
      },
      columnStyles: {
        0: { halign: 'left', cellWidth: 26 },
        1: { halign: 'left', cellWidth: 20 },
        2: { halign: 'right', cellWidth: 10 },
        3: { halign: 'right' },
        4: { halign: 'right' },
        5: { halign: 'right' },
        6: { halign: 'right', fontStyle: 'bold' },
        7: { halign: 'left', cellWidth: 20 },
        8: { halign: 'left', cellWidth: 22 },
      },
      didParseCell: (data) => {
        if (data.section !== 'body') return;
        const m = rowMeta[data.row.index];
        if (!m) return;

        // Muted dashes
        if (data.cell.text[0] === '—' || data.cell.text[0] === '–') {
          data.cell.styles.textColor = C.fg3;
        }
        // Status column: suppress text — drawn manually as pill
        if (data.column.index === 0) data.cell.text = [''];
        // Listing type: suppress text — drawn manually as chip
        if (data.column.index === 8) data.cell.text = [''];
        // List price column with delta needs left align (delta drawn on right)
        if (data.column.index === 3 && m.delta) data.cell.styles.halign = 'left';
        // Incentives accent
        if (data.column.index === 5 && data.cell.text[0] !== '—' && data.cell.text[0] !== '–') {
          data.cell.styles.textColor = C.coral;
          data.cell.styles.fontStyle = 'bold';
        }
      },
      didDrawCell: (data) => {
        if (data.section === 'head') {
          doc.setDrawColor(...C.navy);
          doc.setLineWidth(0.5);
          doc.line(data.cell.x, data.cell.y + data.cell.height,
            data.cell.x + data.cell.width, data.cell.y + data.cell.height);
          return;
        }
        if (data.section !== 'body') return;

        // Body row separator
        doc.setDrawColor(...C.divider);
        doc.setLineWidth(0.18);
        doc.line(data.cell.x, data.cell.y + data.cell.height,
          data.cell.x + data.cell.width, data.cell.y + data.cell.height);

        const m = rowMeta[data.row.index];
        if (!m) return;

        // Sold row: danger stripe on column 0
        if (data.column.index === 0 && m.isSold) {
          doc.setFillColor(...C.danger);
          doc.rect(data.cell.x, data.cell.y + 0.5, 0.9, data.cell.height - 1, 'F');
        }

        // Status pill — column 0 (active → green, sold → red)
        if (data.column.index === 0) {
          const lbl = (m.status || '—').toUpperCase();
          const dotColor = m.isSold ? C.danger : C.success;
          const fg = m.isSold ? C.danger : C.success;
          doc.setFillColor(...dotColor);
          doc.circle(data.cell.x + 3.6, data.cell.y + data.cell.height / 2, 0.85, 'F');
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(baseFontSize - 0.6);
          doc.setTextColor(...fg);
          doc.text(lbl, data.cell.x + 5.2, data.cell.y + data.cell.height / 2 + 0.8);
        }

        // List price delta on right of cell
        if (data.column.index === 3 && m.delta) {
          const arrow = m.delta > 0 ? '+' : '-';
          const pctStr = `${arrow}${m.deltaLabel}`;
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(baseFontSize - 1.2);
          doc.setTextColor(...(m.delta > 0 ? C.success : C.danger));
          doc.text(pctStr, data.cell.x + data.cell.width - 1.5,
            data.cell.y + data.cell.height / 2 + 0.6, { align: 'right' });
        }

        // Listing type chip — column 8
        if (data.column.index === 8 && m.listingType) {
          const lbl = m.listingType;
          // Same yellow / blue / red split as the on-screen pill.
          const chip = listingTypePdfColors(lbl);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(baseFontSize - 1.4);
          const tw = doc.getTextWidth(lbl);
          const chipH = 3.6;
          const chipX = data.cell.x + 1.5;
          const chipY = data.cell.y + (data.cell.height - chipH) / 2;
          doc.setDrawColor(...chip.border);
          doc.setLineWidth(0.2);
          doc.setFillColor(...chip.bg);
          // Fully rounded, to match the pill in the table.
          doc.roundedRect(chipX, chipY, tw + 3.6, chipH, chipH / 2, chipH / 2, 'FD');
          doc.setTextColor(...chip.fg);
          doc.text(lbl, chipX + 1.8, chipY + 2.5);
        }
      },
    });

    // ---- Empty state message if no rows ----
    if (!tableRows.length) {
      const ey = ((doc.lastAutoTable && doc.lastAutoTable.finalY) || y) + 14;
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(9);
      doc.setTextColor(...C.fg3);
      doc.text('No price events recorded for this unit.', PW / 2, ey, { align: 'center' });
    }

    // ---- Util bar + footer on every page ----
    const total = doc.internal.getNumberOfPages();
    for (let i = 1; i <= total; i++) {
      doc.setPage(i);
      drawUtilBar();
      drawPageFooter(i, total);
    }

    window.open(
      doc.output('bloburl'),
      '_blank',
      'toolbar=no,status=no,menubar=no,scrollbars=no,resizable=no,modal=yes,top=100,left=500,width=1000,height=800',
    );
  } catch (error) {
    console.error('Unit history PDF generation error:', error);
  }
};
</script>

<style scoped>
/* ── Card shell ──────────────────────────────────────────── */
.uph-card {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 24px 56px rgba(18, 40, 63, 0.2), 0 8px 16px rgba(18, 40, 63, 0.08);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 88vh;
  font-family: 'Helvetica Light', Helvetica, 'Helvetica Neue', Arial, sans-serif;
  color: #12283F;
}

/* ── Header ──────────────────────────────────────────────── */
.uph-head {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 18px 18px 16px 22px;
  border-bottom: 1px solid #E9E8E9;
  background: #fff;
}

.uph-accent-bar {
  width: 4px;
  height: 38px;
  border-radius: 2px;
  background: #FC5868;
  flex: none;
  margin-top: 2px;
}

.uph-head-text { flex: 1; min-width: 0; }

.uph-eyebrow {
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: #343376;
}

.uph-head-row {
  display: flex;
  align-items: baseline;
  gap: 11px;
  margin-top: 4px;
  flex-wrap: wrap;
}

.uph-unit-link {
  font-family: 'Poppins', sans-serif;
  font-weight: 700;
  font-size: 24px;
  color: #12283F;
  letter-spacing: -0.02em;
  text-decoration: none;
  transition: color 120ms;
}
.uph-unit-link:hover { color: #14134F; text-decoration: underline; }
.uph-unit-link--plain { cursor: default; }
.uph-unit-link--plain:hover { color: #12283F; text-decoration: none; }

.uph-sub-badge {
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 12px;
  color: #12283F;
  background: #EDF2FE;
  border-radius: 5px;
  padding: 4px 9px;
  line-height: 1;
}

.uph-listed {
  font-family: 'Helvetica Light', Helvetica, sans-serif;
  font-size: 12.5px;
  color: #545454;
}

.uph-add-btn {
  background: transparent !important;
  border: 1px solid #C9CCD2 !important;
  color: #12283F !important;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 12px;
  padding: 6px 12px !important;
  border-radius: 6px !important;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.uph-add-btn:hover { background: #F7F8FB !important; }

.uph-close-btn {
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  border-radius: 8px;
  border: 1px solid #E9E8E9;
  background: #fff;
  color: #545454;
  cursor: pointer;
  flex: none;
  transition: background 120ms;
}
.uph-close-btn:hover { background: #F7F8FB; color: #12283F; }

/* ── Spec strip ──────────────────────────────────────────── */
.uph-specs {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 0;
  padding: 14px 22px;
  background: #F7F8FB;
  border-bottom: 1px solid #E9E8E9;
}

.uph-spec-group { display: flex; align-items: center; }

.uph-spec-divider {
  width: 1px;
  height: 26px;
  background: #E9E8E9;
  margin: 0 18px;
}

.uph-spec-label {
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 9.5px;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: #8A8A8A;
  white-space: nowrap;
}

.uph-spec-value {
  font-family: 'Helvetica Light', Helvetica, sans-serif;
  font-size: 14px;
  color: #12283F;
  margin-top: 3px;
}
.uph-spec-value--cap { text-transform: capitalize; }

/* ── Summary band ────────────────────────────────────────── */
.uph-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 16px 22px;
  border-bottom: 1px solid #E9E8E9;
  background: #fff;
}

.uph-stat {
  flex: 1 1 0;
  min-width: 130px;
  padding: 2px 4px;
}

.uph-stat-label {
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: #8A8A8A;
}

.uph-stat-row {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-top: 5px;
}

.uph-stat-val {
  font-family: 'Poppins', sans-serif;
  font-weight: 700;
  font-size: 20px;
  color: #12283F;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  display: inline-block;
  margin-top: 5px;
}

.uph-stat-row .uph-stat-val { margin-top: 0; }

.uph-stat-val--success { color: #1F9D55; }

.uph-stat-sub {
  font-family: 'Helvetica Light', Helvetica, sans-serif;
  font-size: 12px;
  color: #545454;
}

/* ── Table ───────────────────────────────────────────────── */
.uph-table-wrap {
  overflow: auto;
  flex: 1;
  background: #fff;
}

.uph-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 760px;
}

.uph-th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #12283F;
  padding: 11px 14px;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: rgba(255, 255, 255, 0.82);
  white-space: nowrap;
  text-align: left;
}
.uph-th--right { text-align: right; }
.uph-th--first { padding-left: 22px; }
.uph-th--last { padding-right: 22px; }

.uph-row {
  transition: background 120ms cubic-bezier(0.2, 0, 0, 1);
}
.uph-row:hover { background: #EDF2FE; }
.uph-row--sold {
  background: rgba(31, 157, 85, 0.05);
  box-shadow: inset 3px 0 0 #1F9D55;
}
.uph-row--sold:hover { background: rgba(31, 157, 85, 0.1); }

.uph-td {
  padding: 14px;
  border-bottom: 1px solid #EEF0F4;
  font-family: 'Helvetica Light', Helvetica, sans-serif;
  font-size: 13px;
  color: #12283F;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  vertical-align: middle;
}
.uph-td--right { text-align: right; }
.uph-td--first { padding-left: 22px; }
.uph-td--last { padding-right: 22px; }
.uph-td--muted { color: #8A8A8A; }
.uph-td--medium { font-weight: 500; }
.uph-td--net { font-weight: 600; color: #12283F; }
.uph-td--accent { color: #FC5868; font-weight: 600; }
.uph-td--actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  border-bottom: 1px solid #EEF0F4;
}

/* Status pill */
.uph-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px 4px 8px;
  border-radius: 999px;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 11px;
  background: rgba(31, 157, 85, 0.12);
  color: #1F9D55;
}
.uph-pill-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #1F9D55;
}
.uph-pill--sold {
  background: rgba(214, 69, 69, 0.12);
  color: #D64545;
}
.uph-pill--sold .uph-pill-dot { background: #D64545; }

/* List price delta */
.uph-list-cell {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
}
.uph-delta {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 10.5px;
}
.uph-delta--down { color: #FC5868; }
.uph-delta--up { color: #1F9D55; }

/* Listing type chip */
/* Listing Type pill: see assets/styles/listing-type-pill.css (global, shared
   with the other tables that render the column). */

/* Action buttons */
.uph-action-btn {
  width: 28px;
  height: 28px;
  display: inline-grid;
  place-items: center;
  border: 1px solid #E9E8E9;
  background: #fff;
  border-radius: 6px;
  color: #545454;
  cursor: pointer;
  transition: background 120ms;
  text-decoration: none;
}
.uph-action-btn:hover { background: #F7F8FB; color: #12283F; }
.uph-action-btn--danger:hover { color: #D43847; border-color: #FC5868; }

.uph-empty {
  padding: 48px 22px;
  text-align: center;
  color: #8A8A8A;
  font-size: 13px;
}

/* ── Footer ──────────────────────────────────────────────── */
.uph-foot {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 13px 18px 13px 22px;
  border-top: 1px solid #E9E8E9;
  background: #F7F8FB;
}

.uph-foot-text {
  font-family: 'Helvetica Light', Helvetica, sans-serif;
  font-size: 13px;
  color: #545454;
}
.uph-foot-text strong { color: #12283F; font-weight: 600; }

.uph-spacer { flex: 1; }

.uph-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 6px;
  border: 1px solid #C9CCD2;
  background: transparent;
  color: #545454;
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 12px;
  transition: background 120ms cubic-bezier(0.2, 0, 0, 1);
  white-space: nowrap;
}
.uph-btn--ghost:hover { background: #EDF2FE; color: #12283F; }
.uph-btn--navy {
  background: #12283F;
  color: #fff;
  border-color: transparent;
}
.uph-btn--navy:hover { background: #1a3454; }

/* ── Responsive ──────────────────────────────────────────── */
@media (max-width: 767.98px) {
  .uph-card { max-height: 94vh; border-radius: 12px; }
  .uph-head {
    padding: 14px 14px 14px 16px;
    flex-wrap: wrap;
    row-gap: 10px;
  }
  .uph-head-row { gap: 8px; }
  .uph-unit-link { font-size: 20px; }
  .uph-specs { padding: 12px 14px; gap: 10px 0; }
  .uph-spec-divider { display: none; }
  .uph-spec-group { flex: 1 1 50%; min-width: 0; padding: 4px 0; }
  .uph-summary { padding: 14px 14px; }
  .uph-stat { flex: 1 1 45%; min-width: 0; }
  .uph-foot { padding: 12px 14px; flex-wrap: wrap; row-gap: 8px; }
  .uph-foot-text { flex: 1 1 100%; }
  .uph-spacer { display: none; }
}

/* ── Dark mode ───────────────────────────────────────────── */
html.dark .uph-card {
  background: #0e1b2c;
  color: #d6e4f0;
  box-shadow: 0 24px 56px rgba(0, 0, 0, 0.5), 0 8px 16px rgba(0, 0, 0, 0.25);
}
html.dark .uph-head { background: #0e1b2c; border-bottom-color: rgba(255, 255, 255, 0.07); }
html.dark .uph-eyebrow { color: #93c5fd; }
html.dark .uph-unit-link,
html.dark .uph-sub-badge { color: #d6e4f0; }
html.dark .uph-sub-badge { background: rgba(255, 255, 255, 0.08); }
html.dark .uph-listed { color: rgba(255, 255, 255, 0.5); }
html.dark .uph-close-btn,
html.dark .uph-action-btn {
  background: #152030;
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.7);
}
html.dark .uph-close-btn:hover,
html.dark .uph-action-btn:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
}
html.dark .uph-add-btn {
  background: transparent !important;
  color: rgba(255, 255, 255, 0.85) !important;
  border-color: rgba(255, 255, 255, 0.18) !important;
}
html.dark .uph-add-btn:hover { background: rgba(255, 255, 255, 0.06) !important; }

html.dark .uph-specs {
  background: #0b1625;
  border-bottom-color: rgba(255, 255, 255, 0.07);
}
html.dark .uph-spec-divider { background: rgba(255, 255, 255, 0.08); }
html.dark .uph-spec-label { color: rgba(255, 255, 255, 0.4); }
html.dark .uph-spec-value { color: #d6e4f0; }

html.dark .uph-summary { background: #0e1b2c; border-bottom-color: rgba(255, 255, 255, 0.07); }
html.dark .uph-stat-label { color: rgba(255, 255, 255, 0.4); }
html.dark .uph-stat-val { color: #d6e4f0; }
html.dark .uph-stat-val--success { color: #34d399; }
html.dark .uph-stat-sub { color: rgba(255, 255, 255, 0.5); }

html.dark .uph-table-wrap { background: #0b1625; }
html.dark .uph-th {
  background: #152030;
  color: rgba(255, 255, 255, 0.6);
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
}
html.dark .uph-row:hover { background: rgba(255, 255, 255, 0.04); }
html.dark .uph-row--sold { background: rgba(52, 211, 153, 0.06); box-shadow: inset 3px 0 0 #34d399; }
html.dark .uph-row--sold:hover { background: rgba(52, 211, 153, 0.1); }
html.dark .uph-td {
  color: rgba(255, 255, 255, 0.78);
  border-bottom-color: rgba(255, 255, 255, 0.05);
}
html.dark .uph-td--muted { color: rgba(255, 255, 255, 0.35); }
html.dark .uph-td--net { color: #d6e4f0; }
html.dark .uph-td--accent { color: #fda4af; }

html.dark .uph-pill { background: rgba(52, 211, 153, 0.14); color: #34d399; }
html.dark .uph-pill-dot { background: #34d399; }
html.dark .uph-pill--sold { background: rgba(248, 113, 113, 0.14); color: #f87171; }
html.dark .uph-pill--sold .uph-pill-dot { background: #f87171; }

html.dark .uph-foot {
  background: #0b1625;
  border-top-color: rgba(255, 255, 255, 0.07);
}
html.dark .uph-foot-text { color: rgba(255, 255, 255, 0.55); }
html.dark .uph-foot-text strong { color: #d6e4f0; }
html.dark .uph-btn {
  border-color: rgba(255, 255, 255, 0.18);
  color: rgba(255, 255, 255, 0.78);
}
html.dark .uph-btn--ghost:hover { background: rgba(255, 255, 255, 0.06); color: #fff; }
html.dark .uph-btn--navy {
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  border-color: rgba(255, 255, 255, 0.18);
}
html.dark .uph-btn--navy:hover { background: rgba(255, 255, 255, 0.18); }

html.dark .uph-empty { color: rgba(255, 255, 255, 0.3); }
html.dark .uph-delta--down { color: #fda4af; }
html.dark .uph-delta--up { color: #34d399; }
</style>
